
import { Point3D } from '../types';
import { UNIT_SIZE } from '../constants';

/**
 * Proyecta un punto 3D a coordenadas 2D de pantalla.
 * X: Horizontal (Derecha +)
 * Y: Vertical (Arriba +)
 * Z: Profundidad (Fondo +)
 */
export const project = (p: Point3D, rotation: { x: number, y: number }): { x: number, y: number } => {
  const ry = (rotation.y * Math.PI) / 180;
  const rx = (rotation.x * Math.PI) / 180;

  // 1. Rotación alrededor del eje Y (plano XZ - horizontal)
  const x1 = p.x * Math.cos(ry) + p.z * Math.sin(ry);
  const z1 = -p.x * Math.sin(ry) + p.z * Math.cos(ry);
  const y1 = p.y;

  // 2. Rotación alrededor del eje X (inclinación para ver desde arriba)
  // Screen X es directamente x1
  // Screen Y es la combinación de la altura real (y1) y la proyección de la profundidad (z1)
  const screenX = x1;
  const screenY = -y1 * Math.cos(rx) + z1 * Math.sin(rx); // El signo negativo en y1 es porque en SVG Y crece hacia abajo

  return {
    x: screenX * UNIT_SIZE,
    y: screenY * UNIT_SIZE
  };
};

/**
 * Ordena objetos para manejar la oclusión correcta.
 */
export const sortObjects = (objects: any[], rotation: { x: number, y: number }) => {
  const ry = (rotation.y * Math.PI) / 180;
  const rx = (rotation.x * Math.PI) / 180;

  const getDepth = (p: Point3D) => {
    // Calculamos la coordenada Z final después de las rotaciones (distancia al observador)
    const z1 = -p.x * Math.sin(ry) + p.z * Math.cos(ry);
    const zFinal = p.y * Math.sin(rx) + z1 * Math.cos(rx);
    return zFinal;
  };

  const withDepth = objects.map(obj => ({
    obj,
    depth: getDepth(obj.position)
  }));

  return withDepth.sort((a, b) => {
    if (Math.abs(a.depth - b.depth) < 0.001) {
      return a.obj.id.localeCompare(b.obj.id);
    }
    // De mayor a menor profundidad (pintar de atrás hacia adelante)
    return b.depth - a.depth;
  }).map(d => d.obj);
};

export const calculateSurfaceArea = (cubes: Point3D[]): number => {
  let totalArea = 0;
  const directions = [
    { x: 1, y: 0, z: 0 }, { x: -1, y: 0, z: 0 },
    { x: 0, y: 1, z: 0 }, { x: 0, y: -1, z: 0 },
    { x: 0, y: 0, z: 1 }, { x: 0, y: 0, z: -1 }
  ];

  const cubeSet = new Set(cubes.map(c => `${c.x},${c.y},${c.z}`));

  cubes.forEach(cube => {
    directions.forEach(dir => {
      const neighbor = `${cube.x + dir.x},${cube.y + dir.y},${cube.z + dir.z}`;
      if (!cubeSet.has(neighbor)) {
        totalArea += 1;
      }
    });
  });

  return totalArea;
};
